// Session/SpecialSessionAnswersTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Table, Text, ActionIcon, Group, Stack, Center, Loader, Alert,
} from '@mantine/core';
import { IconAlertCircle, IconPhoto, IconClipboardOff, IconRefresh } from '@tabler/icons-react';
import {
  useRegistrationService,
  RegistrationCustomSessionDataModelView,
} from '@actschurch/ccms-sdk';
import type { FieldOption } from '@/components/advanced/FormBuilder/types/BuilderField';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import { EmptyState } from '../Components/EmptyState';

export interface SpecialSessionAnswersTabProps {
  registrationSessionID: number;
  /** The registration's members, to show a 'member' answer by name rather than memberID. */
  memberOptions?: FieldOption[];
}

function formatValue(row: RegistrationCustomSessionDataModelView): string {
  if (row.valueBoolean !== null && row.valueBoolean !== undefined) return row.valueBoolean ? 'Yes' : 'No';
  if (row.valueDecimal !== null && row.valueDecimal !== undefined) return String(row.valueDecimal);
  if (row.valueNumber !== null && row.valueNumber !== undefined) return String(row.valueNumber);
  if (row.valueDateTime !== null && row.valueDateTime !== undefined) return String(row.valueDateTime);
  return row.valueText ?? row.value ?? '—';
}

/** 'member' answers store a memberID; show the member's name when we have it. */
function formatAnswer(row: RegistrationCustomSessionDataModelView, memberOptions: FieldOption[]): string {
  const value = formatValue(row);
  if (row.field?.fieldTypeCode !== 'member' || value === '—') return value;
  return memberOptions.find((o) => o.value === value)?.label ?? `Member #${value}`;
}

function isImageAnswer(row: RegistrationCustomSessionDataModelView): boolean {
  const value = formatValue(row);
  if (!value || value === '—') return false;

  const lowerValue = value.toLowerCase();
  const isUrl = lowerValue.startsWith('http://') || lowerValue.startsWith('https://');
  if (!isUrl) return false;

  if (row.field?.fieldTypeCode === 'url') return true;

  try {
    const decodedValue = decodeURIComponent(lowerValue);
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(decodedValue);
    if (hasImageExtension) return true;
  } catch (e) {
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(lowerValue);
    if (hasImageExtension) return true;
  }

  if (lowerValue.includes('s3.af-south-1.amazonaws.com/temp/uploads')) {
    return true;
  }

  return false;
}

export function SpecialSessionAnswersTab({ registrationSessionID, memberOptions = [] }: SpecialSessionAnswersTabProps) {
  const registrationService = useRegistrationService();

  const [rows, setRows] = useState<RegistrationCustomSessionDataModelView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Image viewer state ──────────────────────────────────────────────────
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState<RegistrationCustomSessionDataModelView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await registrationService.getCustomSessionData(registrationSessionID);
      setRows(data);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load answers.');
    } finally {
      setLoading(false);
    }
  }, [registrationService, registrationSessionID]);

  useEffect(() => { load(); }, [load]);

  const openViewer = (row: RegistrationCustomSessionDataModelView) => {
    setViewerRow(row);
    setViewerOpen(true);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const viewerFiles = viewerRow
    ? [
        {
          id: String(viewerRow.registrationCustomSessionDataID),
          url: formatValue(viewerRow),
          fileName: viewerRow.field?.label ?? 'Image',
          mimeType: 'image/*',
          fileSize: 0,
        },
      ]
    : [];

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Group justify="flex-end">
        <ActionIcon
          variant="subtle"
          aria-label="Refresh answers"
          onClick={load}
          loading={loading}
          size="lg"
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>

      {rows.length === 0 ? (
        <EmptyState
          icon={IconClipboardOff}
          title="No answers captured yet"
          description="Once someone fills out this session's form, their answers will show up here."
          compact
        />
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Question</Table.Th>
              <Table.Th>Answer</Table.Th>
              <Table.Th>Participant</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.registrationCustomSessionDataID}>
                <Table.Td>{row.field?.label ?? '—'}</Table.Td>
                <Table.Td>
                  {isImageAnswer(row) ? (
                    <Group
                      gap={4}
                      style={{ cursor: 'pointer', display: 'inline-flex' }}
                      onClick={() => openViewer(row)}
                    >
                      <IconPhoto size={14} color="var(--mantine-color-blue-filled)" />
                      <Text size="sm" c="blue" td="underline">
                        View image
                      </Text>
                    </Group>
                  ) : (
                    formatAnswer(row, memberOptions)
                  )}
                </Table.Td>
                <Table.Td>{row.memberName ?? '—'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerRow(null);
        }}
        files={viewerFiles}
        initialIndex={0}
        showGallery={false}
      />
    </Stack>
  );
}

export default SpecialSessionAnswersTab;